import { useTranslation } from "react-i18next";
import { PublicCompany, selectCompany } from "../../homeSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  Avatar,
  ButtonDS,
  Chips,
  DownloadOutlined,
  EditOutlined,
  IconCircle,
  InputDS,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  PendingActionsOutlined,
  Spacer,
  TextCapitalized,
  fromToDateFormatterDayMonthLongYear,
  hasEmailFormat,
  sortByDateTime,
  triggerToastDS,
  usePersistMuiTableState,
} from "@qivia/ui";
import {
  PublicStatementsDisplayed,
  PublicStatements,
  UpdateCompanyType,
} from "../billingsAPI";
import {
  billingsSlice,
  selectStatementExportLink,
  selectStatementExportStatus,
  selectStatementsList,
  selectStatementsListStatus,
  documentDownloadAsync,
  statementExportAsync,
  statementsListAsync,
  supportingDocumentsZipFolderExportAsync,
  updateRecipientEmailAsync,
  deleteRecipientEmailAsync,
  selectStatementDownloadStatus,
  selectSupportingDocumentsZipFolderExportStatus,
} from "../billingsSlice";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useParams } from "react-router-dom";
import "../../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { getDate } from "../../../../libSlice/materialUiFormats";

const EmailDisplay = (props: {
  onClick: () => void;
  company: PublicCompany;
  isInputEmailOpen: boolean;
  setIsEmailInputOpen: (val: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);

  useEffect(() => {
    if (props.company) setRecipientEmails(props.company.recipientsEmails);
  }, [props.company]);

  const clickToDelete = useCallback(
    (email: string) => {
      const payload: UpdateCompanyType = {
        uuid: props.company.uuid,
        recipientsEmails: recipientEmails.filter(
          (recipientEmail) => recipientEmail !== email,
        ),
      };
      if (payload.recipientsEmails.length !== 0)
        void dispatch(deleteRecipientEmailAsync(payload));
    },
    [dispatch, recipientEmails, props.company.uuid],
  );

  return (
    <StyledEmailDisplay>
      {recipientEmails.map((recipientEmail) => {
        return (
          <>
            <Chips
              size={"L"}
              text={recipientEmail}
              leftElement={
                <Avatar color={"green"} firstName={recipientEmail} size={"S"} />
              }
              onCrossClick={() => clickToDelete(recipientEmail)}
            />
            <Spacer x={0.5} />
          </>
        );
      })}
      {props.isInputEmailOpen ? (
        <EmailInput
          company={props.company}
          onClick={() => props.setIsEmailInputOpen(false)}
        />
      ) : (
        <ButtonDS
          buttonType={"secondary"}
          format={"hug"}
          singleIcon={{ icon: <EditOutlined />, size: "S" }}
          onClick={props.onClick}
        />
      )}
    </StyledEmailDisplay>
  );
};

const EmailInput = (props: { onClick: () => void; company: PublicCompany }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [recipientEmailInput, setRecipientEmailInput] = useState<string>("");

  useEffect(() => {
    if (props.company) setRecipientEmails(props.company.recipientsEmails);
  }, [props.company]);

  const isValidForm: boolean = useMemo(
    () =>
      hasEmailFormat(recipientEmailInput) &&
      recipientEmailInput !== "" &&
      !recipientEmails.includes(recipientEmailInput),
    [recipientEmails, recipientEmailInput],
  );

  const onSubmit = useCallback(
    (emails: string[]) => {
      const payload: UpdateCompanyType = {
        uuid: props.company.uuid,
        recipientsEmails: emails,
      };
      void dispatch(updateRecipientEmailAsync(payload));
      props.onClick();
    },
    [dispatch, props],
  );

  const clickToSubmit = useCallback(() => {
    setDisplayError(true);
    isValidForm && onSubmit([...recipientEmails, recipientEmailInput]);
  }, [isValidForm, recipientEmails, onSubmit, recipientEmailInput]);

  const getEmailError = useCallback(() => {
    if (!displayError) return undefined;
    if (recipientEmailInput === "") {
      return t("billings.emailsFrame.placeholder.error.empty");
    } else if (!hasEmailFormat(recipientEmailInput)) {
      return t("billings.emailsFrame.placeholder.error.format");
    } else if (recipientEmails.includes(recipientEmailInput)) {
      return t("billings.emailsFrame.placeholder.error.duplicate");
    } else return undefined;
  }, [displayError, recipientEmails, recipientEmailInput, t]);

  return (
    <StyledEmailInput>
      <InputDS
        placeholder={t("billings.emailsFrame.placeholder")}
        error={getEmailError()}
        value={recipientEmailInput}
        onChange={(e) => setRecipientEmailInput(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
        withoutSpacerBottom
        height={2.18}
      />
      <Spacer x={0.5} />
      <StyledButton>
        <ButtonDS
          sizeButton={"S"}
          text={t("billings.emailsFrame.save")}
          format={"hug"}
          buttonType={"secondary"}
          onClick={clickToSubmit}
          disabled={!isValidForm}
        />
      </StyledButton>
    </StyledEmailInput>
  );
};

export const EmailsFrame = (props: { company: PublicCompany }) => {
  const { t } = useTranslation();
  const [isInputEmailOpen, setIsEmailInputOpen] = useState<boolean>(false);

  return (
    <StyledEmailsFrameContainer>
      <Spacer y={1} />
      <StyledEmailContent>
        <Spacer x={1.5} />
        <StyledColumn>
          <Spacer y={1.5} />
          <StyledFlex>
            <IconCircle
              icon={<PendingActionsOutlined />}
              backgroundColor={"accent500"}
              iconColor={"textWhite"}
            />
            <Spacer x={0.75} />
            <StyledHeader>
              <StyledTitle>
                <TextCapitalized>
                  {t("billings.emailsFrame.title")}
                </TextCapitalized>
              </StyledTitle>
              <Spacer y={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t(`billings.emailsFrame.display.subTitle`)}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledHeader>
          </StyledFlex>
          <Spacer y={1.5} />
          <StyledBody>
            <EmailDisplay
              onClick={() => setIsEmailInputOpen(true)}
              company={props.company}
              isInputEmailOpen={isInputEmailOpen}
              setIsEmailInputOpen={setIsEmailInputOpen}
            />
          </StyledBody>
          <Spacer y={1.5} />
        </StyledColumn>
        <Spacer x={1.5} />
      </StyledEmailContent>
    </StyledEmailsFrameContainer>
  );
};

export const StatementTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);

  const [billingsListSorted, setBillingsListSorted] = useState<
    PublicStatementsDisplayed[] | null
  >([]);
  const statementsListStatus = useAppSelector(selectStatementsListStatus);
  const statementDownloadStatus = useAppSelector(selectStatementDownloadStatus);
  const statementsListInit: PublicStatements[] =
    useAppSelector(selectStatementsList);
  const statementExportLink = useAppSelector(selectStatementExportLink);
  const supportingDocumentsZipFolderExportAsyncStatus = useAppSelector(
    selectStatementExportStatus,
  );
  const supportingDocumentsZipFolderExportStatus = useAppSelector(
    selectSupportingDocumentsZipFolderExportStatus,
  );

  const pageName = "statements";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amount: "sum",
      fileName: "size",
      date: "size",
      bucketFileNameDownload: "size",
      invoiceBucketFileNameDownload: "size",
      zipFolderBucketFileNameDownload: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const keysToTranslate = useMemo(
    () => ["date"] as (keyof PublicStatementsDisplayed)[],
    [],
  );

  const findKeysToTranslate = useCallback(
    (
      key: keyof PublicStatementsDisplayed,
      value: string,
      statement: PublicStatements,
    ) => {
      switch (key) {
        case "date":
          if (statement.fromDate && statement.toDate) {
            return value
              ? fromToDateFormatterDayMonthLongYear(
                  new Date(statement.fromDate),
                  new Date(statement.toDate),
                )
              : "";
          }
          return value;
        default:
          return value;
      }
    },
    [],
  );

  const statementsList: (PublicStatements & { id: string })[] = useMemo(() => {
    const rowsWithId = statementsListInit.map((statement) => {
      let newDict: PublicStatements & {
        id: string;
      } = {
        ...statement,
        id: statement.uuid,
      };
      Object.entries(statement).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof PublicStatementsDisplayed)) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(
                  key as keyof PublicStatementsDisplayed,
                  value as string,
                  statement,
                )
              : "",
          };
        }
      });
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc", "fromDate");
  }, [statementsListInit, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (company && statementsListStatus === "idle") {
      void dispatch(statementsListAsync(company.uuid));
    }
  }, [dispatch, company, statementsListStatus]);

  useEffect(() => {
    if (statementExportLink.presignedUrl && statementExportLink.fileName) {
      void dispatch(documentDownloadAsync(statementExportLink));
    }
  }, [statementExportLink, dispatch]);

  useEffect(() => {
    if (statementDownloadStatus === "success") {
      triggerToastDS(t("billings.statements.upload.success") || "", "SUCCESS");
      dispatch(billingsSlice.actions.resetStatementsExportStatus());
    } else if (statementDownloadStatus === "failed") {
      triggerToastDS(t("billings.statements.upload.failure") || "", "ERROR");
      dispatch(billingsSlice.actions.resetStatementsExportStatus());
    }
  }, [dispatch, statementDownloadStatus, t]);

  //handle loading during download of supporting documents zip folder
  useEffect(() => {
    if (
      supportingDocumentsZipFolderExportStatus === "success" &&
      statementDownloadStatus === "processing"
    ) {
      triggerToastDS(
        t("billings.statements.upload.processing") || "",
        "DOWNLOADING",
      );
    }
  }, [
    dispatch,
    statementDownloadStatus,
    supportingDocumentsZipFolderExportStatus,
    t,
  ]);

  useEffect(() => {
    if (supportingDocumentsZipFolderExportAsyncStatus === "failed") {
      triggerToastDS(t("billings.zipfolder.export.error") || "", "ERROR");
      dispatch(billingsSlice.actions.resetStatementsExportStatus());
    }
  }, [dispatch, supportingDocumentsZipFolderExportAsyncStatus, t]);

  useEffect(() => {
    if (statementsList && statementsListStatus === "success") {
      const billingsDateTimeSorted = sortByDateTime(
        statementsList,
        "desc",
        "fromDate",
      );
      setBillingsListSorted(billingsDateTimeSorted);
    }
  }, [statementsList, statementsListStatus]);

  const columnsDisplayed: {
    [index in keyof PublicStatementsDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      date: {
        type: "date",
        width: 200,
        valueGetter: getDate,
        valueFormatter: (value: string, row: unknown) => {
          const rowData = row as PublicStatements;
          if (rowData.fromDate && rowData.toDate) {
            return (
              <TextCapitalized>
                {fromToDateFormatterDayMonthLongYear(
                  new Date(rowData.fromDate),
                  new Date(rowData.toDate),
                )}
              </TextCapitalized>
            );
          }
          return "";
        },
      },
      amount: {
        type: "number",
        width: 100,
        valueFormatter: (value: number) => {
          return value && <StyledAmount>{value + " EUR"}</StyledAmount>;
        },
      },
      bucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      statementExportAsync({
                        bucketFileName: value ?? "",
                        fileName:
                          "fileName" in row ? (row["fileName"] as string) : "",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
      invoiceBucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      statementExportAsync({
                        bucketFileName: value ?? "",
                        fileName: `${"fileName" in row ? (row["fileName"] as string).split(".xlsx")[0] + ".pdf" : ""}`,
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
      zipFolderBucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    void dispatch(
                      supportingDocumentsZipFolderExportAsync({
                        zipFolderUuid: value,
                        companyName: company?.name ?? "",
                        date:
                          "fromDate" in row
                            ? (row["fromDate"] as string)
                            : new Date().toISOString(),
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
    };
  }, [dispatch, company]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("statements.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (params.tab !== "statements") {
    return;
  }

  const searchBarProps = {
    values: statementsList,
    setFilterValues: setBillingsListSorted,
    keysToIgnore: [
      "bucketFileNameDownload" as const,
      "invoiceBucketFileNameDownload" as const,
      "zipFolderBucketFileNameDownload" as const,
    ],
  };

  if (!billingsListSorted) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<keyof PublicStatementsDisplayed, PublicStatementsDisplayed>
      rows={billingsListSorted}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledEmailDisplay = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
`;

const StyledBody = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const StyledButton = styled.div`
  width: 7rem;
`;

const StyledEmailsFrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Header/H2"]}
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const StyledEmailContent = styled.div`
  display: flex;
  border-radius: 0.5rem;
  background: ${colors["colors/surfaces/background/background_level1"]};
  width: 100%;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledTitle = styled.div`
  ${typographies["Body/XL"]};
`;

const StyledSubTitle = styled.div`
  ${typographies["Body/XS"]};
`;

const StyledAmount = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledEmailInput = styled.div`
  display: flex;
  align-items: center;
  min-width: 30rem;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
